import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import StructuredData from "./components/StructuredData";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
// Lazy load blog pages to avoid loading KaTeX/Parse5 on homepage
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
import CaseStudies from "./pages/CaseStudies";
import FAQ from "./pages/FAQ";
import Support from "./pages/Support";
import Websites from "./pages/services/Websites";
import MobileApps from "./pages/services/MobileApps";
import AITools from "./pages/services/AITools";
import Automation from "./pages/services/Automation";
import Analytics from "./pages/services/Analytics";
import CustomSolutions from "./pages/services/CustomSolutions";
import OasisAds from "./pages/OasisAds";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import CookiePolicy from "./pages/CookiePolicy";

function Router() {
  const [location] = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
      <Switch>
        <Route path={"/"} component={Home} />
        <Route path={"/blog"} component={Blog} />
        <Route path={"/blog/:slug"} component={BlogPost} />
      <Route path={"/case-studies"} component={CaseStudies} />
      <Route path={"/faq"} component={FAQ} />
      <Route path={"/support"} component={Support} />
      <Route path={"/services/websites"} component={Websites} />
      <Route path={"/services/mobile-apps"} component={MobileApps} />
      <Route path={"/services/ai-tools"} component={AITools} />
      <Route path={"/services/automation"} component={Automation} />
      <Route path={"/services/analytics"} component={Analytics} />
      <Route path={"/services/custom-solutions"} component={CustomSolutions} />
      <Route path={"/oasis-ads"} component={OasisAds} />
      <Route path={"/privacy-policy"} component={PrivacyPolicy} />
      <Route path={"/terms-conditions"} component={TermsConditions} />
      <Route path={"/cookie-policy"} component={CookiePolicy} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider
          defaultTheme="dark"
          // switchable
        >
          <TooltipProvider>
            <StructuredData />
            <Toaster />
            <Router />
          </TooltipProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
